import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { toast } from "sonner";

const OperationsContext = createContext(null);

export function OperationsProvider({ children }) {
  // Map: key -> { label, startedAt }
  const [operations, setOperations] = useState(new Map());

  const startOp = useCallback((key, label) => {
    setOperations((prev) => {
      const next = new Map(prev);
      next.set(key, { label, startedAt: Date.now() });
      return next;
    });
  }, []);

  const endOp = useCallback((key) => {
    setOperations((prev) => {
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const hasRunning = operations.size > 0;
  const runningList = Array.from(operations.values());

  return (
    <OperationsContext.Provider value={{ operations, startOp, endOp, hasRunning, runningList }}>
      {children}
    </OperationsContext.Provider>
  );
}

export function useOperations() {
  return useContext(OperationsContext);
}

/**
 * Hook: useTrackedOperation
 * Zwraca funkcje `track(key, label, asyncFn)` — opakowuje async call:
 *  - rejestruje operacje globalnie (startOp/endOp)
 *  - zapobiega state update po unmount (mountedRef)
 *  - pokazuje tost sukcesu/bledu
 */
export function useTrackedOperation() {
  const { startOp, endOp } = useOperations();
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const track = useCallback(
    async (key, label, asyncFn) => {
      startOp(key, label);
      try {
        const result = await asyncFn();
        if (mountedRef.current) {
          toast.success(`${label} — terminé`);
        } else {
          toast.info(`${label} — terminé (retour à l'onglet)`);
        }
        return { result, mounted: mountedRef.current };
      } catch (err) {
        const msg = err?.response?.data?.detail || err.message || "Erreur";
        if (mountedRef.current) {
          toast.error(`${label} — erreur : ${msg}`);
        } else {
          toast.error(`${label} — erreur en arrière-plan : ${msg}`);
        }
        throw err;
      } finally {
        endOp(key);
      }
    },
    [startOp, endOp]
  );

  return { track, mountedRef };
}
